import { Button, Container, Switch, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import PageTitle from "../../../components/pageTitle/PageTitle";
import { Card, CardContent, CardHeader, Checkbox, FormControlLabel, FormGroup, useMediaQuery } from "@mui/material";
import styles from "../Accounts.module.scss";
import { getSettings, resetSettings, saveSettings, updateSettings } from "../../../services/apiservices/settings";
import AuthContext from "../../../hooks/authContext";
import CommonContext from "../../../hooks/commonContext";
import { useNavigate } from "react-router";
import { CommonConfirmationDialog, CommonSuccessDialog } from "../../../components/DialogComponent/SupplyDataDialog";
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useToast } from "../../../hooks/useToast";
import { AlertCircle, MessageSquare } from "lucide-react";
import SMSComplianceAttestation from "./SMSCompilance";


 const UsageLimitCard = ({ used, total, percentage, remaining }) => {
    const getColorClass = () => {
      if (percentage >= 90) return 'from-red-500 to-red-600';
      if (percentage >= 75) return 'from-amber-500 to-orange-600';
      return 'from-blue-500 to-indigo-600';
    };

    const getBackgroundColor = () => {
      if (percentage >= 90) return 'from-red-50 to-red-50 dark:from-red-950/30 dark:to-red-950/30';
      if (percentage >= 75) return 'from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30';
      return 'from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30';
    };

    const getAlertColor = () => {
      if (percentage >= 90) return { 
        bg: 'bg-red-50 dark:bg-red-950/40', 
        border: 'border-red-200 dark:border-red-800', 
        text: 'text-red-800 dark:text-red-300', 
        subtext: 'text-red-600 dark:text-red-400', 
        icon: 'text-red-600 dark:text-red-400' 
      };
      if (percentage >= 75) return { 
        bg: 'bg-amber-50 dark:bg-amber-950/40', 
        border: 'border-amber-200 dark:border-amber-800', 
        text: 'text-amber-800 dark:text-amber-300', 
        subtext: 'text-amber-600 dark:text-amber-400', 
        icon: 'text-amber-600 dark:text-amber-400' 
      };
      return null;
    };


    const alertColors = getAlertColor();

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-24 hover:shadow-md transition-shadow dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]  dark:text-white dark:border-none" style={{width:"100%"}}>
        <div className="flex items-center gap-3 mb-24">
          <div className={`bg-gradient-to-br ${getColorClass()} p-10 rounded-lg`}>
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">SMS Notifications</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Current billing period</p>
          </div>
        </div>

        <div className={`bg-gradient-to-r ${getBackgroundColor()} rounded-lg p-16 mb-16`}>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs text-gray-600 mb-4 dark:text-gray-300">Used</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                {used || 0}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600 mb-4 dark:text-gray-300">Total Limit</p>
              <p className="text-lg font-semibold text-gray-600 dark:text-gray-200">
                {total || 0}
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${getColorClass()} rounded-full transition-all duration-500 relative`}
                style={{ width: `${percentage}%` }}
              >
                <div className="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
              </div>
            </div>
            <div className="flex justify-between mt-8 text-xs">
              <span className="text-gray-600 dark:text-gray-300 font-medium">{percentage}% used</span>
              <span className="text-gray-500 dark:text-gray-400">{(remaining || 0)} remaining</span>
            </div>
          </div>
        </div>

        {percentage >= 75 && alertColors && (
          <div className={`flex items-start gap-2 ${alertColors.bg} border ${alertColors.border} rounded-lg p-12`}>
            <AlertCircle className={`w-4 h-4 ${alertColors.icon} flex-shrink-0 mt-2`} />
             <div>
              <p className={`text-xs font-medium ${alertColors.text}`}>
                {percentage >= 100 
                  ? 'Limit Reached' 
                  : percentage >= 95
                  ? 'Critical: Almost finished'
                  : percentage >= 90 
                  ? 'Critical: Near limit' 
                  : 'Warning: High usage'}
              </p>
              <p className={`text-xs ${alertColors.subtext} mt-0.5`}>
                {percentage >= 100
                  ? `You have used all of your SMS quota. Service has been stopped.`
                  : percentage >= 95
                  ? `You have used ${percentage}% of your quota. Only ${remaining} SMS notifications remaining.`
                  : percentage >= 90 
                  ? `You have used ${percentage}% of your quota. Service may be interrupted soon.`
                  : `You've used ${percentage}% of your SMS allocation for this period.`}
              </p>
            </div>
          </div>
        )}

        {percentage < 75 && (
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-12">
            <span>Status: Normal</span>
            <span className="text-green-600 dark:text-green-400 font-medium">✓ Within limit</span>
          </div>
        )}
      </div>
    );
  };


const NotificationPageSetting = () => {
    const isMobile = useMediaQuery('(max-width:767px)');
    const [smsRoles, setSMSRoles] = useState([]);
    const [emailRoles, setEmailRoles] = useState([]);
    const [pushNotificationRoles, setPushNotificationRoles] = useState([]);
    const [notificationLimits, setNotificationLimits] = useState(null);
    const [error, setError] = useState({});
    const [data, setData] = useState();
    const auth = useContext(AuthContext);
    const commonContext = useContext(CommonContext);
    const [open, setOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetSuccessOpen, setResetSuccessOpen] = useState(false);
    const [isAttested, setIsAttested] = useState(false);
    const [smsConsent, setSmsConsnet] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const userTypes = [
        { label: "ADMIN USER", value: "admin" },
        { label: "SUPPORT USER", value: "support" }
    ];

    const [emailEnabled, setEmailEnabled] = useState(false);
    const [smsEnabled, setSMSEnabled] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);

    const [openSections, setOpenSections] = useState({
        email: false,
        sms: false,
        push: false,
    });


    const fetchSettings = async () => {
      const companyId = auth?.userProfile?.role === "companyUser" ? auth?.userProfile?.id : auth?.userProfile?.companyId;

      const getNotificationLimitsPromise = new Promise((resolve) => {
        getSettings("notification_limits", companyId, (res) => {
          if (res?.data) {
            const filteredData = res?.data?.filter((item) => !item.deleted);
            resolve(filteredData?.length > 0 ? filteredData[0] : null);
          } else {
            resolve(null);
          }
        });
      });

      const getNotificationSettingsPromise = new Promise((resolve) => {
        getSettings("notification_settings", companyId, (res) => {
          if (res?.data) {
            const filteredData = res?.data?.filter((item) => !item.deleted);
            resolve(filteredData?.length > 0 ? filteredData : []);
          } else {
            resolve([]);
          }
        });
      });

      const [limitsResult, settingsResult] = await Promise.all([
        getNotificationLimitsPromise,
        getNotificationSettingsPromise,
      ]);

      setNotificationLimits(limitsResult);

      setSmsConsnet(limitsResult?.checked ? true : false);
      setIsAttested(limitsResult?.checked ? true : false);
          
      if (settingsResult?.length > 0) {
        const [firstItem] = settingsResult;
        setData(settingsResult);
        setSMSRoles(firstItem?.smsRoles);
        setEmailRoles(firstItem?.emailRoles);
        setPushNotificationRoles(firstItem?.pushNotificationRoles);
        setEmailEnabled(firstItem?.emailRoles?.length > 0);
        setSMSEnabled(firstItem?.smsRoles?.length > 0);
        setPushEnabled(firstItem?.pushNotificationRoles?.length > 0);
        setOpenSections({
          email: firstItem?.emailRoles?.length > 0,
          sms: firstItem?.smsRoles?.length > 0,
          push: firstItem?.pushNotificationRoles?.length > 0,
        });
      } else {
        setData([]);
        setSMSRoles([]);
        setEmailRoles([]);
        setPushNotificationRoles([]);
        setEmailEnabled(false);
        setSMSEnabled(false);
        setPushEnabled(false);
        setOpenSections({ email: false, sms: false, push: false });
      }
    };


    useEffect(() => {
        fetchSettings();
    }, []);

    const validFields = () => {
        const errormessages = {};
        let isValid = true;

        if (!smsRoles.length && (!emailRoles.length || emailRoles.length === 0) && (!pushNotificationRoles.length || pushNotificationRoles.length === 0)) {
            isValid = false;
            errormessages.sms = "SMS Roles is Required";
            errormessages.email = "Email Roles is Required";
            errormessages.push = "Push Notification Roles is Required";
        }

        setError(errormessages);
        return isValid;
    };

    const saveData = () => {
      if (notificationLimits?.checked === smsConsent) return;

      const userInfo = {
        id: auth?.userProfile?.id,
        name: `${auth?.userProfile?.firstName} ${auth?.userProfile?.lastName}`,
      };

      const detailToSave = smsConsent
        ? {
            checked: true,
            checkedBy: userInfo,
          }
        : {
            checked: false,
            unCheckedBy: userInfo,
          };

      if (notificationLimits?.id) {
        updateSettings(
          detailToSave,
          "notification_limits",
          notificationLimits.id
        );
      }
    };

    const buildNotificationPayload = () => ({
      smsRoles,
      emailRoles,
      pushNotificationRoles,
    });

    const handleSaveData = async () => {
      try {
        commonContext?.setLoader(true);

        const companyId =
          auth?.userProfile?.role === "companyUser"
            ? auth?.userProfile?.id
            : auth?.userProfile?.companyId;

        const payload = {
          ...buildNotificationPayload(),
          companyId,
        };

        await saveData();

        const response = await saveSettings(payload, "notification_settings");

        if (response?.status) {
          await fetchSettings();
          toast?.({
            title: "Success",
            description: "Settings saved successfully",
          });
        }
      } catch (error) {
        console.error("Save notification settings failed", error);
      } finally {
        commonContext?.setLoader(false);
      }
    };

    const handleUpdateData = async () => {
      try {
        commonContext?.setLoader(true);

        const payload = buildNotificationPayload();
        const settingsId = data?.[0]?.id;

        if (!settingsId) return;

        await saveData();

        const response = await updateSettings(
          payload,
          "notification_settings",
          settingsId
        );

        if (response?.status) {
          await fetchSettings();
          toast?.({
            title: "Success",
            description: "Settings updated successfully",
          });
        }
      } catch (error) {
        console.error("Update notification settings failed", error);
      } finally {
        commonContext?.setLoader(false);
      }
    };


    const handleResetConfirm = async () => {
        setResetOpen(false);
        commonContext?.setLoader(true);
        const id = data[0]?.id;
        const details = await resetSettings('notification_settings', id);
        if (details.status === true) {
          await fetchSettings();
          toast?.({title: "Success", description: "Setting Reset Successfully"});
          commonContext?.setLoader(false);
        }
        commonContext?.setLoader(false);
    }

    const handleResetClose = () => {
        setResetOpen(false);
    }

    const handleResetSuccessClose = () => {
        setResetSuccessOpen(false);
        fetchSettings();
        toast?.({title: "Success", description: "Setting Reset Successfully"});
    }

    const handleColse = () => {
        setOpen(false);
        navigate("/account/settings");
        toast?.({title: "Success", description: "Setting Saved Successfully"});
    }

    const handleUpdateClose = () => {
        setUpdateOpen(false);
        navigate("/account/settings");
        toast?.({title: "Success", description: "Setting Update Successfully"});
    }

    const handleReset = () => {
        if (data) {
            setResetOpen(true);
        } else {
            setEmailRoles([]);
            setData();
            setSMSRoles([]);
            setPushNotificationRoles([]);
            setError({});
            setEmailEnabled(false);
            setSMSEnabled(false);
            setPushEnabled(false);
        }
    };


    const handleToggle = (type) => {
        const updatedState = { ...openSections, [type]: !openSections[type] };
        setOpenSections(updatedState);

        if (type === "email") setEmailEnabled(!emailEnabled);
        if (type === "sms") setSMSEnabled(!smsEnabled);
        if (type === "push") setPushEnabled(!pushEnabled);
    };

    const handleRoleChange = (type, role) => {
        const setter =
            type === "email" ? setEmailRoles :
                type === "sms" ? setSMSRoles :
                    setPushNotificationRoles;

        const roles =
            type === "email" ? emailRoles :
                type === "sms" ? smsRoles :
                    pushNotificationRoles;

        const updated = roles.includes(role)
            ? roles.filter((r) => r !== role)
            : [...roles, role];

        setter(updated);
    };

    const handleToggleAllUsers = (type) => {
        const setter =
            type === "email" ? setEmailRoles :
                type === "sms" ? setSMSRoles :
                    setPushNotificationRoles;

        const roles =
            type === "email" ? emailRoles :
                type === "sms" ? smsRoles :
                    pushNotificationRoles;

        const allValues = userTypes.map(user => user.value);
        const shouldClear = roles.length === allValues.length;

        setter(shouldClear ? [] : allValues);
    };

    const renderSection = (type, IconComponent, title, description, enabled, roles) => (
        <>
            <div className={`${styles.notificationSection} d-flex flex-direction-column shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
                <FormControlLabel
                    className={styles.notificationToggle}
                    label={
                        <div className={styles.labelGroup}>
                            <IconComponent
                                fontSize="small"
                                className={
                                    type === "email"
                                        ? styles.iconEmail
                                        : type === "sms"
                                            ? styles.iconSMS
                                            : styles.iconPush
                                }
                            />
                            <div>
                                <Typography className={`${styles.labelTitle} dark:text-white`}>{title}</Typography>
                                <Typography className={styles.labelDescription}>{description}</Typography>
                            </div>
                        </div>
                    }
                    labelPlacement="start"
                    control={
                        <Switch
                            color="primary"
                            checked={enabled}
                            onChange={() => handleToggle(type)}
                        />
                    }
                />
            </div>
            {error[type] && (
                <Typography
                  variant="body2"
                  color="error"
                  className="mt-4 ml-48"
                >
                  {error[type]}
                </Typography>
              )}
            {openSections[type] && (
                <div className={styles.userTypesContainer}>
                    <div className={styles.userTypeHeader}>
                        <Typography className={`${styles.userTypeLabel} dark:text-white`}>User Types</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            className={`${styles.toggleAllButton} dark:text-white`}
                            onClick={() => handleToggleAllUsers(type)}
                        >
                            Toggle All
                        </Button>
                    </div>
                    <FormGroup className={`${styles.notificationSection} mt-8 shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:border-0`}>
                        {userTypes.map(({ label, value }) => (
                            <FormControlLabel
                                key={value}
                                control={
                                    <Checkbox
                                        sx={{
                                            '&.Mui-checked': {
                                                color: '#078037',
                                            },
                                            '&.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#078037',
                                            }
                                        }}
                                        checked={roles.includes(value)}
                                        onChange={() => handleRoleChange(type, value)}
                                    />
                                }
                                className="dark:text-white text-[#020817]"
                                label={label}
                            />
                        ))}
                    </FormGroup>
                </div>
            )}
        </>
    );

    const total = notificationLimits?.smsPackageLimits || 0;
    const used =  notificationLimits?.smsRemainingAllowance || 0;
    const remaining = total - used;

    const percentageUsed = total > 0 ? (used / total) * 100 : 0;
    const roundedPercentage = Math.round(percentageUsed); 

    return (
      <>
        <Container>
          <PageTitle
            pageTitle="Notification Settings"
            showBreadcrumb={[
              { label: "Home", to: "/" },
              { label: "Settings", to: "/account/settings" },
            ]}
            BreadCrumbCurrentPageTitle={"Notification Settings"}
            showLastSeen={true}
          />
          <div className="sectionBox dark:bg-[#1A1F2C]">
          <div>
            <div className="mb-8">
              <h2 className="text-foreground text-xl">Notification Settings</h2>
              <p className="text-sm text-[#64748B] mt-2">
                Manage how you receive notifications and updates.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

              <Card className={`${styles.notificationCard} dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:text-white dark:border-none`}>
                <CardHeader
                  title={
                    <span className="text-xl text-foreground">
                      Notification Preferences
                    </span>
                  }
                  subheader={
                    <span className="text-sm text-[#64748B]">
                      Manage who can send notifications and through which channels.
                      Select the user roles that are allowed to notify employees via
                      email, SMS or push notifications.
                    </span>
                  }
                  className={styles.cardHeader}
                />

                <CardContent className={styles.cardContent}>
                  {renderSection(
                    "email",
                    MarkunreadOutlinedIcon,
                    "Email Notifications",
                    "Enable or disable the ability for selected user roles to send notifications via email.",
                    emailEnabled,
                    emailRoles
                  )}

                  {renderSection(
                    "sms",
                    ChatBubbleOutlineOutlinedIcon,
                    "SMS Notifications",
                    "Enable or disable SMS notifications for selected user roles.",
                    smsEnabled,
                    smsRoles
                  )}

                  {renderSection(
                    "push",
                    NotificationsNoneOutlinedIcon,
                    "Push Notifications",
                    "Enable or disable mobile app push notifications.",
                    pushEnabled,
                    pushNotificationRoles
                  )}

                </CardContent>
              </Card>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Usage Limits
                </h3>

                <p className="text-sm text-gray-600 dark:text-[#64748B]">
                  Track your SMS notification usage for the current billing period.
                </p>

                <UsageLimitCard
                  used={used}
                  total={total}
                  percentage={roundedPercentage}
                  remaining={remaining}
                />
              </div>
            </div>

            <div className="mt-12">
              <SMSComplianceAttestation
                onEnableSMS={() => setSmsConsnet(true)}
                onDisableSMS={() => setSmsConsnet(false)}
                isSMSEnabled={smsConsent}
                styles={styles}
                setIsAttested={setIsAttested}
                isAttested={isAttested}
              />
            </div>

            <div className="mt-12 flex gap-3">
              <Button
                type="submit"
                variant="contained"
                style={{ color: "white", backgroundColor: "#078037" }}
                onClick={data ? handleUpdateData : handleSaveData}
              >
                Save Changes
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={() => navigate("/account/settings")}
                className="dark:text-white"
              >
                Cancel
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={handleReset}
                className="dark:text-white"
              >
                Reset to Default
              </Button>
            </div>
          </div>
        </div>

        </Container>
        {open && (
          <CommonSuccessDialog
            open={open}
            onClose={handleColse}
            title={"Success"}
            message={"Your settings have been successfully saved."}
          />
        )}
        {updateOpen && (
          <CommonSuccessDialog
            open={updateOpen}
            onClose={handleUpdateClose}
            title={"Success"}
            message={"Your settings have been successfully update."}
          />
        )}
        {resetOpen && (
          <CommonConfirmationDialog
            open={resetOpen}
            onConfirm={handleResetConfirm}
            onClose={handleResetClose}
            title={"Setting Reset"}
            message={"Are you sure you want to reset the settings?"}
            buttonTitle={"Yes"}
          />
        )}
        {resetSuccessOpen && (
          <CommonSuccessDialog
            open={resetSuccessOpen}
            onClose={handleResetSuccessClose}
            title={"Success"}
            message={"Your settings have been successfully reset."}
          />
        )}
      </>
    );

}
export default NotificationPageSetting;